import { HttpClient } from '@angular/common/http';
import { environment } from '@deltager/organizers/admin/environments/environment';
import { map, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { PaymentType } from '../models/PaymentType';
import { EditBookingData, SystemFieldTypeEnum } from '../components/edit-booking-popup/models/edit-booking-data.model';
import { BookingEvent, mapBookingEvent } from '../models/booking-event.model';
import { AuthService } from '../../core/auth/auth.service';
import { DeleteRefundType } from '@deltager/organizers/admin/shared/enums/delete-refund-type.enum';

@Injectable({
  providedIn: 'root',
})
export class BaseBookingApiService {
  constructor(
    public http: HttpClient,
    public authService: AuthService,
  ) {}

  public resendConfirmation(orderDetailId: number, request: ResendConfirmationRequest): Observable<boolean> {
    return this.http.post<boolean>(
      `${environment.apiBaseURL}/booking-participants/${orderDetailId}/resend-confirmation`,
      request,
    );
  }

  public getDeleteDetails(orderDetailId: number): Observable<BookingRegistrationDeleteDetails> {
    return this.http.get<BookingRegistrationDeleteDetails>(
      `${environment.apiBaseURL}/booking-participants/${orderDetailId}/delete-details`,
    );
  }

  public deleteRefundBooking(
    orderDetailId: number,
    request: DeleteBookingRegistrationRequest,
  ): Observable<BookingDeleteRefundResult> {
    return this.http.patch<BookingDeleteRefundResult>(
      `${environment.apiBaseURL}/booking-participants/${orderDetailId}/delete-refund`,
      request,
    );
  }

  public getBookingInfo(orderDetailId: number, sessionId: number): Observable<EditBookingData> {
    return this.http
      .get<EditBookingData>(`${environment.apiBaseURL}/booking-participants/${orderDetailId}/edit-details/${sessionId}`)
      .pipe(
        map(res => {
          res?.systemFields.forEach(i => (i.isCustom = false));
          res?.customFields.forEach(i => (i.isCustom = true));
          return res;
        }),
      );
  }

  public getById(bookingEventId: number): Observable<BookingEvent> {
    return this.http
      .get<BookingEvent>(`${environment.apiBaseURL}/booking-events/${bookingEventId}`)
      .pipe(map(be => mapBookingEvent(be)));
  }

  public updateBookingRegistration(eventData: IEditBookingPatch): Observable<any> {
    return this.http.patch(`${environment.apiBaseURL}/booking-participants/${eventData.orderDetailId}`, eventData, {
      headers: this.authService.getAuthHttpHeaders(),
    });
  }
}

export interface ResendConfirmationRequest {
  orderId: number;
  email: string;
}

export interface BookingRegistrationDeleteDetails {
  orderDetailId: number;
  orderId: number;
  firstName: string | null;
  lastName: string | null;
  title: string;
  price: number;
  paidDate: string | null;
  outpaid: number;
  isEnoughFunds: boolean;
  totalFees: number;
  isPaid: boolean;
  paymentMethod?: PaymentType;
  refundedAmount: number;
}

export interface DeleteBookingRegistrationRequest {
  refundAmount: number;
  deleteRefundType: DeleteRefundType;
  keepInTheList: boolean;
}

export enum BookingDeleteRefundResult {
  Deleted = 1,
  Refunded = 2,
}

export interface IEditBookingPatch {
  orderDetailId?: string | number;
  sessionId?: number;
  notes?: string;
  resendConfirmation: boolean;
  selectedTimeSlot?: {
    from: string;
    to: string;
    date: string;
    priceCategoryId: number;
    venueId: number;
    availabilityTimeSlotId?: number;
    availabilityDayId?: number;
  };
  customFieldAnswers?: CustomFieldAnswer[];
  systemFieldAnswers?: SystemFieldAnswer[];
}

export interface SystemFieldAnswer {
  type: SystemFieldTypeEnum;
  value: string;
}

export interface CustomFieldAnswer {
  id?: number;
  customFieldId: number | string;
  value: string | boolean;
}
